import React, {useEffect, useState} from 'react';
import './EligibilityVerificationStyles.scss';
import {useTranslation} from 'react-i18next';

import {fetchInsuranceCoverages} from '@shared/services/InsuranceEligibilityServices';
import InsurancePatientDetailsCard from './InsurancePatientDetailsCard/InsurancePatientDetailsCard';
import InsuranceSubscriberDetailsCard from './InsuranceSubscriberDetailsCard/InsuranceSubscriberDetailsCard';
import InsurancePayerDetailsCard from './InsurancePayerDetailsCard/InsurancePayerDetailsCard';
import InsuranceProviderDetailsCard from './InsuranceProviderDetailsCard/InsuranceProviderDetailsCard';
import InsurancePlanDetailsTable from './InsurancePlanDetailsTable/InsurancePlanDetailsTable';
import {Benefit, Coverage, Plan} from './EligibilityVerificationMapper';
import VerifyInsurance from './VerifyInsurance';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  CardContent,
  Grid,
  Typography,
  makeStyles
} from '@material-ui/core';
import Loader from '@components/framework/loader/Loader';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import HelperUtils from '@shared/utils/HelperUtils';
import InsuranceCard from './InsuranceCard';
import {getClinicGroupsDetails} from '@shared/services/ClinicServices';
import {AppPermissions, BenefitNetworkKeys} from '@shared/constants/Mapping';
import KeyValueCard from '@components/system/KeyValueCard/KeyValueCard';
import {CARD_BOX_SHADOW} from 'src/style/variable';
import {itemWithNetworkKeys} from './InsurancePlanDetailsTable/BenefitInformation';
import {_setEligibilityVerificationNetworkPatientDrawer} from '@shared/store/actions/PatientDrawerAction';
import {useDispatch, useSelector} from 'react-redux';
import InsuranceCardImages from './InsuranceCardImages/InsuranceCardImages';

const EligibilityVerificationDetailsCard = makeStyles(() => ({
  section: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '4rem'
  },
  sectionItem: {
    alignItems: 'center',
    gap: '0.5rem',
    display: 'grid',
    gridTemplateColumns: 'max-content min-content 1fr',
    columnGap: '1rem',
    rowGap: '1rem',
    width: '50%',
    padding: '1rem'
  },
  label: {
    color: '#54585b'
  },
  title: {
    fontSize: '1.3rem',
    fontWeight: 600,
    paddingBlock: '1rem'
  },
  cardRoot: {
    '& .MuiCardContent-root:last-child': {
      paddingBottom: '13px'
    },
    boxShadow: CARD_BOX_SHADOW,
    borderRadius: '1rem'
  },
  infoWrapper: {
    marginBottom: '1rem'
  },
  benefitAccordionSummary: {
    flexDirection: 'row-reverse',
    paddingLeft: 0,
    '& .MuiAccordionSummary-content': {
      marginLeft: '1rem'
    }
  }
}));
const EligibilityVerification = (props: any): React.ReactElement => {
  const {groupId, clinicId, patientId, appointment}: any = props;
  const classes = EligibilityVerificationDetailsCard();
  const [groupDetails, setGroupDetails]: any = useState({});
  const [insuranceState, setInsuranceState] = useState<{
    insuranceData: {
      coverages: Coverage[];
      // eslint-disable-next-line
      plan_details: Record<string, any> | null;
    };
    isLoadingInsuranceData: boolean;
  }>({
    insuranceData: {coverages: [], plan_details: null},
    isLoadingInsuranceData: true
  });
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const patientDetailsDrawer = useSelector(
    (state: any) => state.patientDetailsDrawer
  );

  let insuranceWithImages = [];
  if (Array.isArray(patientDetailsDrawer?.patientDetails?.insurance)) {
    insuranceWithImages = patientDetailsDrawer?.patientDetails?.insurance.filter(
      (item: any) => item?.['file_id1'] && item?.['file_id2']
    );
  }
  const getPatientEligibilityDetails = async () => {
    try {
      setInsuranceState(p => ({...p, isLoadingInsuranceData: true}));
      const response = await fetchInsuranceCoverages(
        groupId,
        clinicId,
        appointment?.id
      );
      setInsuranceState(p => ({...p, insuranceData: response?.data as any}));
    } catch (error: any) {
      enqueueSnackbar(
        error?.message
          ? error.message
          : t(`ELIGIBILITY_VERIFICATION.ERROR_IN_GETTING_ELIGIBILITY_DETAILS`),
        SnackBarConfig('e')
      );
    } finally {
      setInsuranceState(p => ({...p, isLoadingInsuranceData: false}));
    }
  };

  const getSelectedGroupDetails = async (): Promise<void> => {
    try {
      getClinicGroupsDetails(groupId)
        .then((clinicGroupDetails: any) => {
          if (clinicGroupDetails?.data) {
            setGroupDetails(clinicGroupDetails.data);
          }
        })
        .catch(error => {
          enqueueSnackbar(
            t('CLINICS.CLINIC_GROUP_DETAILS_ERROR'),
            SnackBarConfig('e')
          );
          throw error;
        });
    } catch (error: any) {
      enqueueSnackbar(
        (error && error.message) || t('CLINICS.CLINIC_GROUP_DETAILS_ERROR'),
        SnackBarConfig('e')
      );
      throw error;
    }
  };

  const dispatch = useDispatch();

  useEffect(() => {
    getSelectedGroupDetails();
    getPatientEligibilityDetails();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const firstCoverage = insuranceState.insuranceData?.coverages?.[0];

  const hasCoverages =
    !insuranceState.isLoadingInsuranceData &&
    insuranceState.insuranceData?.coverages?.length > 0;

  const hasPlanDetails =
    !insuranceState.isLoadingInsuranceData &&
    // Should have plan details
    insuranceState.insuranceData?.plan_details;

  const renderNoDataInfo = () => {
    return (
      <div className="no-data-found">
        {t('ELIGIBILITY_VERIFICATION.NO_COVERAGES_FOUND')}
        <VerifyInsurance
          clinicGroupId={groupId}
          clinicId={clinicId}
          patientId={patientId}
          appointment={appointment}
          hasCoverages={hasCoverages}
          inAppointmentCard={false}
          etaDate={appointment?.coverage_eta_date}
          isEligibilityVerificationEnabled={
            groupDetails[
              AppPermissions.MENU_PERMISSIONS.ELIGIBILITY_VERIFICATION
            ]
          }
          onRefreshInsurance={getPatientEligibilityDetails}
        />
      </div>
    );
  };

  const renderManualPlanDetails = () => {
    const manualPlanLeftRowData = [
      {
        label: t(
          'ELIGIBILITY_VERIFICATION.MANUAL_VERIFY_INSURANCE_FORM.INSURANCE_NAME'
        ),
        value: insuranceState.insuranceData?.plan_details?.insurance_name
      },
      {
        label: t(
          'ELIGIBILITY_VERIFICATION.MANUAL_VERIFY_INSURANCE_FORM.SUBSCRIBER_GROUP_NUMBER'
        ),
        value:
          insuranceState.insuranceData?.plan_details?.subscriber_group_number
      },
      {
        label: t(
          'ELIGIBILITY_VERIFICATION.MANUAL_VERIFY_INSURANCE_FORM.PLAN_START_DATE'
        ),
        value: HelperUtils.formatDisplayDate(
          insuranceState.insuranceData?.plan_details?.plan_start_date
        )
      },
      {
        label: t(
          'ELIGIBILITY_VERIFICATION.MANUAL_VERIFY_INSURANCE_FORM.POLICY_NUMBER'
        ),
        value: insuranceState.insuranceData?.plan_details?.policy_number
      }
    ];

    const manualPlanRightRowData = [
      {
        label: t(
          'ELIGIBILITY_VERIFICATION.MANUAL_VERIFY_INSURANCE_FORM.SUBSCRIBER_MEMBER_ID'
        ),
        value: insuranceState.insuranceData?.plan_details?.subscriber_member_id
      },
      {
        label: t(
          'ELIGIBILITY_VERIFICATION.MANUAL_VERIFY_INSURANCE_FORM.PLAN_NAME'
        ),
        value: insuranceState.insuranceData?.plan_details?.plan_name
      },
      {
        label: t(
          'ELIGIBILITY_VERIFICATION.MANUAL_VERIFY_INSURANCE_FORM.PLAN_END_DATE'
        ),
        value: HelperUtils.formatDisplayDate(
          insuranceState.insuranceData?.plan_details?.plan_end_date
        )
      },
      {
        label: t(
          'ELIGIBILITY_VERIFICATION.MANUAL_VERIFY_INSURANCE_FORM.PLAN_NUMBER'
        ),
        value: insuranceState.insuranceData?.plan_details?.plan_number
      }
    ];
    return (
      <div className="section-header">
        <Typography className={classes.title}>
          {t('ELIGIBILITY_VERIFICATION.PLAN_DETAILS')}
        </Typography>
        <Card className={`${classes.cardRoot}s`}>
          <CardContent>
            <div className={classes.section}>
              <KeyValueCard data={manualPlanLeftRowData} />
              <KeyValueCard data={manualPlanRightRowData} />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  useEffect(() => {
    // go over the coverages, in each coverage plans, each plan has benefits, and each benefit may have networks
    if (Array.isArray(insuranceState.insuranceData?.coverages)) {
      const benefitNetworks = new Set<string>();
      insuranceState.insuranceData.coverages.map(coverage => {
        if (Array.isArray(coverage.plans)) {
          coverage.plans.map((plan: Plan) => {
            if (Array.isArray(plan.benefits)) {
              plan.benefits.map((benefit: Benefit) => {
                itemWithNetworkKeys.forEach(item => {
                  const itemWithNetworksObj = item.getItem(benefit);
                  if (itemWithNetworksObj) {
                    const networkKeys = Object.keys(itemWithNetworksObj);
                    networkKeys.forEach(key => {
                      if (BenefitNetworkKeys.includes(key as any)) {
                        benefitNetworks.add(key);
                      }
                    });
                  }
                });
              });
            }
          });
        }
      });
      if (Array.from(benefitNetworks).length === 1) {
        dispatch(
          _setEligibilityVerificationNetworkPatientDrawer(
            Array.from(benefitNetworks)[0]
          )
        );
      } else {
        _setEligibilityVerificationNetworkPatientDrawer(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insuranceState.insuranceData?.coverages]);

  return (
    <div className="eligibility-wrapper">
      {insuranceState.isLoadingInsuranceData ? (
        <Loader />
      ) : (
        <>
          {hasCoverages && (
            <div>
              <div className="link-style-wrapper">
                <VerifyInsurance
                  clinicGroupId={groupId}
                  clinicId={clinicId}
                  patientId={patientId}
                  appointment={appointment}
                  hasCoverages={hasCoverages}
                  inAppointmentCard={false}
                  etaDate={appointment?.coverage_eta_date}
                  isEligibilityVerificationEnabled={
                    groupDetails[
                      AppPermissions.MENU_PERMISSIONS.ELIGIBILITY_VERIFICATION
                    ]
                  }
                  onRefreshInsurance={getPatientEligibilityDetails}
                />
              </div>
              <Grid container spacing={1} className={classes.infoWrapper}>
                {insuranceWithImages.length > 0 && (
                  <Grid item xs={12}>
                    <InsuranceCardImages
                      clinicId={patientDetailsDrawer?.clinicId}
                      patientId={
                        patientDetailsDrawer?.patientDetails?.patient_id
                      }
                      groupId={patientDetailsDrawer?.groupId}
                      insurances={insuranceWithImages}
                    />
                  </Grid>
                )}
              </Grid>
              {/* Subscriber, patient, payer, provider an insurance will be same for all coverages.
              So, rendering all the details from the first coverages */}
              {firstCoverage && (
                <Grid container spacing={2} className={classes.infoWrapper}>
                  <Grid item xs={12} md={6}>
                    <InsuranceSubscriberDetailsCard
                      subscriber={firstCoverage?.subscriber}
                      insuranceSource={firstCoverage?.patient_insurance?.source}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InsurancePatientDetailsCard
                      patient={firstCoverage?.patient}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InsurancePayerDetailsCard coverage={firstCoverage} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InsuranceProviderDetailsCard
                      requestingProvider={firstCoverage?.requesting_provider}
                    />
                  </Grid>
                  {firstCoverage?.patient_insurance && (
                    <Grid item xs={12} sm={6}>
                      <InsuranceCard
                        insurance={firstCoverage.patient_insurance}
                        effectiveDate={firstCoverage.effective_date}
                      />
                    </Grid>
                  )}
                </Grid>
              )}
              {Array.isArray(insuranceState.insuranceData?.coverages) &&
                insuranceState.insuranceData?.coverages.length > 0 && (
                  <div className="coverages-section">
                    {/* Show Accordion only  if its length is greater than 1 */}
                    {insuranceState.insuranceData?.coverages.length > 1 &&
                      insuranceState.insuranceData?.coverages.map(
                        (coverage: Coverage, index: number) => {
                          return (
                            // Expand the first coverage by default
                            <Accordion
                              key={index}
                              defaultExpanded={index === 0 ? true : false}>
                              <AccordionSummary
                                className={`header ${classes.benefitAccordionSummary}`}
                                expandIcon={<ExpandMoreIcon />}>
                                {`${index + 1}. ${t(
                                  'ELIGIBILITY_VERIFICATION.COVERAGE_INFORMATION'
                                )}`}
                              </AccordionSummary>
                              <AccordionDetails>
                                {Array.isArray(coverage?.plans) &&
                                  coverage.plans.length > 0 && (
                                    <InsurancePlanDetailsTable
                                      expandFirstPlan={
                                        index === 0 ? true : false
                                      }
                                      plans={coverage.plans}
                                    />
                                  )}
                              </AccordionDetails>
                            </Accordion>
                          );
                        }
                      )}
                    {insuranceState.insuranceData?.coverages.length === 1 &&
                      Array.isArray(
                        insuranceState.insuranceData?.coverages[0]?.plans
                      ) &&
                      insuranceState.insuranceData?.coverages?.[0].plans
                        .length > 0 && (
                        <InsurancePlanDetailsTable
                          plans={
                            insuranceState.insuranceData?.coverages[0].plans
                          }
                          expandFirstPlan={true}
                        />
                      )}
                  </div>
                )}
            </div>
          )}
          {!hasCoverages && hasPlanDetails && renderManualPlanDetails()}
          {!hasCoverages && !hasPlanDetails && renderNoDataInfo()}
        </>
      )}
    </div>
  );
};

export default EligibilityVerification;
